import { useEffect, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { InputFieldWithInnerLabel } from "src/components/ui/inputFields/input-field-with-label";
import { registerNewUser } from "../../api/userInfo";
import { BACKGROUND_VIDEO } from "../../shared/config";
import { login } from "../../context/auth/auth_action";
import { AuthContext } from "../../context/auth/auth_context";
import { useLocation } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

const SignUpPage = () => {
  const { t } = useTranslation("main");
  const authContext = useContext(AuthContext);
  const location = useLocation();
  const domain = window?.location?.hostname;
  const { dispatch } = authContext;
  const [form, setForm] = useState({
    first_name: undefined,
    last_name: undefined,
    email: undefined,
    username: undefined,
    phone: undefined,
    is_accepted_active_consent: undefined,
  });

  const [verify, setVerify] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [terms, setTerms] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState(undefined);
  const [errorMsg, setErrorMsg] = useState(false);
  const queryParams = new URLSearchParams(location.search);
  const referralCode = queryParams.get("c");

  const isUrlFrasers = domain.includes("frasers");

  const [errors, setErrors] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    terms: "",
  });

  const validateField = (field, value) => {
    let error = "";
    switch (field) {
      case "first_name":
        if (!value) error = `${t("register.first_name_required")}`;
        break;
      case "last_name":
        if (!value) error = `${t("register.last_name_required")}`;
        break;
      case "email":
        if (!value?.includes("@")) {
          error = `${t("register.email_error")}`;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = `${t("register.email_required")}`;
        }
        break;
      case "confirmPassword":
        if (value !== form.password)
          error = `${t("register.password_not_match")}`;
        break;
      default:
        break;
    }
    return error;
  };

  const onInput = (field) => (e) => {
    const value = e.target.value;
    let error = "";

    if (field === "passwordConfirm") {
      setConfirmPassword(value);
    } else {
      error = validateField(field, value);
      setForm({
        ...form,
        [field]: value,
      });

      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: error,
      }));
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};
    Object.keys(form).forEach((field) => {
      const error = validateField(field, form[field]);
      if (error) newErrors[field] = error;
    });

    if (!form?.phone || !/^\d{10,15}$/.test(form.phone)) {
      newErrors.phone = `${t("register.error_message3")}`;
    }

    if (!terms) {
      newErrors.terms = `${t("register.error_message2")}`;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (confirmPassword === form?.password) {
      if (terms) {
        const form_data = {
          ...form,
          is_accepted_active_consent: terms,
          username: form.email,
        };

        try {
          const queryParam = referralCode ? `?c=${referralCode}` : "";
          const { data, error } = await registerNewUser(form_data, queryParam);

          const formLogin = {
            username: form_data.username,
            password: form_data.password,
          };

          if (data) {
            login(dispatch, formLogin);
          } else {
            // setErrorMsg(error.response.data.detail);
            if (error.response.data.field === "password") {
              setErrors((prevErrors) => ({
                ...prevErrors,
                password: error.response.data.detail,
              }));
            } else {
              setErrorMsg(error.response.data.detail);
            }
          }
        } catch (error) {
          setErrorMsg(`${t("register.error_message")}`);
        }
      }
    } else {
      setErrors(() => ({
        confirmPassword: `${t("register.password_not_match")}`,
      }));
    }
  };

  useEffect(() => {
    if (errorMsg) {
      let error_msg = setTimeout(() => setErrorMsg(undefined), 3000);
      return () => {
        clearTimeout(error_msg);
      };
    }
  }, [errorMsg]);

  return (
    <div className="relative flex items-center justify-center w-full h-screen overflow-hidden bg-black bg-no-repeat bg-cover">
      <video
        autoPlay
        loop
        muted
        className="fixed z-0 w-auto min-w-full min-h-full max-w-none"
      >
        <source src={BACKGROUND_VIDEO} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
      <div className="absolute inset-0  w-auto min-w-full min-h-full opacity-0 bg-vekin max-w-none"></div>
      <div
        className={`flex flex-col justify-between p-8 bg-white shadow-md rounded-md w-[536px] z-10 ${verify ? "hidden" : "block"}`}
      >
        <h2 className="text-4xl font-bold text-vekin">
          {t("register.sign_up")}
        </h2>
        {errorMsg && (
          <span className="text-sm text-danger flex justify-center">
            {errorMsg}
          </span>
        )}
        <form onSubmit={onSubmit} autoComplete="off">
          <section className="flex flex-col w-full gap-4 pr-3 mt-2">
            <InputFieldWithInnerLabel
              errorText={errors.first_name}
              type="text"
              label={`${t("register.first_name")}`}
              labelClassName={"text-vekin font-normal"}
              onChange={onInput("first_name")}
              value={form.first_name}
              maxLength={50}
            />

            <InputFieldWithInnerLabel
              errorText={errors.last_name}
              type="text"
              label={`${t("register.last_name")}`}
              labelClassName={"text-vekin font-normal"}
              value={form.last_name}
              onChange={onInput("last_name")}
              maxLength={50}
            />

            <InputFieldWithInnerLabel
              errorText={errors?.email}
              type="email"
              label={`${t("register.email")}`}
              labelClassName={"text-vekin font-normal"}
              value={form.email}
              onChange={onInput("email")}
              maxLength={50}
            />

            <div className="relative mt-1">
              <label
                className="text-vekin text-xs capitalize absolute left-12 mt-0.5 z-50"
                htmlFor="phone"
              >
                {t("register.phone_number")}
              </label>
              <PhoneInput
                country={"th"}
                value={form.phone}
                errorText={errors?.phone}
                countryCodeEditable={false}
                onChange={(phone) =>
                  onInput("phone")({ target: { value: phone } })
                }
                placeholder="Enter phone number"
                inputProps={{
                  name: "phone",
                  required: true,
                  autoFocus: false,
                  maxLength: 15,
                }}
                inputStyle={{
                  width: "100%",
                  height: "50px",
                  fontSize: "14px",
                  borderRadius: "6px",
                  paddingTop: "14px",
                  paddingLeft: "48px",
                }}
                containerStyle={{
                  width: "100%",
                }}
              />
              {errors.phone && (
                <p className="absolute text-[11px] text-red-400 left-0 top-12.5">
                  {errors.phone}
                </p>
              )}
            </div>

            <div className="relative">
              <div className={`flex flex-row w-full gap-6 `}>
                <InputFieldWithInnerLabel
                  errorText={errors?.password}
                  type="password"
                  label={`${t("register.password")}`}
                  labelClassName={"text-vekin font-normal"}
                  value={form.password}
                  onChange={onInput("password")}
                />

                <InputFieldWithInnerLabel
                  errorText={errors?.confirmPassword}
                  type="password"
                  label={`${t("register.re_enter_password")}`}
                  labelClassName={"text-vekin font-normal"}
                  value={confirmPassword}
                  onChange={onInput("passwordConfirm")}
                />
              </div>
            </div>

            <fieldset>
              <legend className="sr-only">Checkbox variants</legend>

              <div className="flex items-start mt-10">
                <input
                  id="termsCondition"
                  type="checkbox"
                  defaultValue={terms}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    setTerms(isChecked);

                    if (isChecked) {
                      setErrors((prevErrors) => ({
                        ...prevErrors,
                        terms: "",
                      }));
                    }
                  }}
                  className="w-4 h-4 rounded accent-emerald-700 focus:ring-3 focus:ring-vekin"
                />
                <label
                  for="termsCondition"
                  className="ml-3 text-sm font-medium text-gray-900"
                >
                  {t("register.i_agree")}{" "}
                  <a
                    href={
                      isUrlFrasers
                        ? "/terms&policy/frasers/ESG_Terms and Conditions (TH).pdf"
                        : "/terms&policy/vekin/thai_terms_of_services_of_carbon_wallet_platform.pdf"
                    }
                    target="_blank"
                    rel="noreferrer"
                    className="font-semibold text-vekin hover:underline"
                  >
                    {t("register.terms_and_conditions")}
                  </a>{" "}
                  {t("register.and")}{" "}
                  <a
                    href={
                      isUrlFrasers
                        ? "/terms&policy/frasers/ESG_Privacy Notice of ESG Management System (TH).pdf"
                        : "/terms&policy/vekin/thai_terms_of_services_of_carbon_wallet_platform.pdf"
                    }
                    target="_blank"
                    rel="noreferrer"
                    className="font-semibold text-vekin hover:underline"
                  >
                    {t("register.privacy_policy")}
                  </a>
                </label>
              </div>
              {errors.terms && (
                <p className="text-red-400 text-xs mt-0">{errors.terms}</p>
              )}
            </fieldset>
            <button
              className="w-full cursor-pointer"
              type="submit"
              style={{
                height: 48,
                marginTop: 24,
                backgroundColor: "#1FA37C",
                padding: 12,
                borderRadius: 10,
                fontSize: 16,
                fontWeight: 500,
                color: "white",
                textAlign: "center",
              }}
            >
              {t("register.sign_up")}
            </button>
            <p className="text-center text-primary">
              {t("register.already_have_account")}
              <a href="/login" className="font-semibold text-center text-vekin">
                {t("register.login")}
              </a>
            </p>
          </section>
        </form>
      </div>
    </div>
  );
};
export default SignUpPage;
